import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

export interface ToolDetailItem {
  name: string;
  executionTimeMs: number;
  /** Tool arguments (e.g. JSON query); may be null when streaming */
  functionCalls?: string | null;
  /** Tool result data (e.g. JSON); may be null when streaming */
  resultData?: string | null;
}

interface ToolsDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: ToolDetailItem[];
  title?: string;
}

function formatExecutionTime(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function tryFormatJson(raw: string): string {
  try {
    const parsed = JSON.parse(raw);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return raw;
  }
}

export function ToolsDetailDialog({
  open,
  onOpenChange,
  items,
  title = 'Tools used',
}: ToolsDetailDialogProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-1 pr-4 -mr-4">
          <div className="space-y-2">
            {items.map((item, index) => {
              const hasDetails =
                (item.functionCalls != null && item.functionCalls.trim() !== '') ||
                (item.resultData != null && item.resultData.trim() !== '');
              const isExpanded = expandedIndex === index;
              const queriesFormatted = item.functionCalls?.trim()
                ? tryFormatJson(item.functionCalls)
                : null;
              const resultFormatted = item.resultData?.trim()
                ? tryFormatJson(item.resultData)
                : null;

              return (
                <div
                  key={`${item.name}-${index}`}
                  className="rounded-lg border border-border/60 bg-muted/30 overflow-hidden"
                >
                  <button
                    type="button"
                    className={cn(
                      'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-muted/50',
                      hasDetails && 'cursor-pointer'
                    )}
                    onClick={() => hasDetails && setExpandedIndex(isExpanded ? null : index)}
                    aria-expanded={hasDetails ? isExpanded : undefined}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-foreground">{item.name}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {formatExecutionTime(item.executionTimeMs)}
                      </span>
                    </span>
                    {hasDetails && (
                      <span className="text-[10px] text-muted-foreground">
                        {isExpanded ? 'Collapse' : 'Expand'}
                      </span>
                    )}
                  </button>
                  {hasDetails && isExpanded && (
                    <div className="border-t border-border/60 px-3 py-2 space-y-3 text-xs">
                      {queriesFormatted != null && (
                        <div>
                          <p className="mb-1 font-medium text-muted-foreground">
                            Query / arguments
                          </p>
                          <pre className="overflow-x-auto rounded bg-muted/80 p-2 font-mono text-[11px] whitespace-pre-wrap break-words">
                            {queriesFormatted}
                          </pre>
                        </div>
                      )}
                      {resultFormatted != null && (
                        <div>
                          <p className="mb-1 font-medium text-muted-foreground">Result data</p>
                          <pre className="max-h-48 overflow-auto rounded bg-muted/80 p-2 font-mono text-[11px] whitespace-pre-wrap break-words">
                            {resultFormatted}
                          </pre>
                        </div>
                      )}
                      {!queriesFormatted && !resultFormatted && (
                        <p className="text-muted-foreground italic">
                          Full details available after the message is saved.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
